import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { commonComponentImages } from '@images/common-components-images';
import { commonImages } from '@images/common-images';
import { AiAssistantVoiceService } from '@modules/ai-assistant/services/ai-assistant-voice.service';
import { ApiService } from '@services/api/api.service';
import { SharedService } from '@services/api/shared.service';
import { AlertService } from '@services/helpers/alert.service';
import { ReduxService } from '@state-management/redux.service';
import { PipeDirectiveModule } from '@shared-modules/pipe-directive.module';
import { ToastrService } from 'ngx-toastr';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ReMultipleSelectModule } from '@common/re-multi-select/re-multiselect.module';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { EMPTY, Subject, Subscription, catchError, debounceTime, distinctUntilChanged, map, of, switchMap } from 'rxjs';

/**
 * Floating "notes" side panel that lives in the app header.
 *
 * Owns the entire take-a-note / notes-list / edit-note experience that previously
 * lived inline in HeaderComponent. The header keeps the trigger icons and toggles
 * this panel via a template ref (#headerNotes) calling openNote().
 */
@Component({
  selector: 'app-header-notes',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, TooltipModule, PipeDirectiveModule, ReMultipleSelectModule],
  templateUrl: './header-notes.component.html',
  styleUrls: ['./header-notes.component.scss'],
})
export class HeaderNotesComponent implements OnInit, OnDestroy {
  images = commonComponentImages;
  common_images = commonImages;

  // Ordered alphabetically by display label (see typeLabel): Contacts, EOI, Leads, Listing, Lots, Other, Projects, Property.
  search_notes_type = [
    { type: 'contacts', icon: this.common_images?.phoneBook_icon, wdth: '20px', margin: '-2px' },
    { type: 'eoi', icon: this.common_images?.cloud_icon, wdth: '17px' },
    { type: 'leads', icon: this.common_images?.lead_icon, wdth: '20px', margin: '-2px' },
    { type: 'listing', icon: this.common_images?.listing_icon, wdth: '16px' },
    { type: 'units', icon: this.common_images?.apartment_icon, wdth: '16px' },
    // { type: 'contract', icon: this.common_images?.contract_icon, wdth: '20px', margin: '-2px' },
    // { type: 'tasks', icon: this.common_images?.task_icon, wdth: '20px', margin: '-2px' },
    { type: 'other' },
    { type: 'projects', icon: this.common_images?.project_1, wdth: '20px', margin: '-2px' },
    { type: 'property', icon: this.common_images?.home_icon, wdth: '17px' },
  ];

  searchBar: any[] = [];
  /** Index into flatSearchItems of the keyboard-highlighted suggestion (-1 = none). */
  highlightedIndex: number = -1;
  isNoteOpen: boolean = true;
  isEdit: boolean = false;
  isNote: boolean = false;
  notesList: any[] = [];
  text: any = null;
  /** True while voice dictation is actively listening (drives the mic/stop toggle). */
  listening: boolean = false;
  /** Active dictation subscription (mirrors the AI assistant composer). */
  private voiceSub?: Subscription;
  /** Editor HTML captured when dictation starts, so speech is appended rather than replacing. */
  private htmlBeforeVoice = '';
  isLoad: any;
  submitted = false;

  rows: number = 15;
  page: number = 1;
  /** Page size for infinite-scroll pagination of the notes list. */
  perPage: number = 10;
  /** True while a page request is in flight (guards against duplicate scroll loads). */
  isLoading: boolean = false;
  /** False once the server returns a partial page (no more notes to load). */
  hasMoreNotes: boolean = true;
  scrollTop: number = 0;
  /** Project id supplied externally (e.g. from a lead screen) — takes precedence over the URL. */
  projectIdOverride: string | null = null;
  /** Lead first name supplied externally (e.g. from a lead screen) — personalises the copied-notes email greeting. */
  leadName: string | null = null;
  /** Lead email supplied externally (e.g. from a lead screen) — used as the recipient of the copied-notes email. */
  leadEmail: string | null = null;

  @ViewChild('customEditorAddNote', { static: false }) customEditorAddNote!: ElementRef;
  @ViewChild('customEditorEditNote', { static: false }) customEditorEditNote!: ElementRef;
  @ViewChild('scrollableElement') scrollableElement!: ElementRef;

  note: any = {
    title: '',
    desc: '',
    id: null,
    type: null,
    isPin: false,
    is_archive: false,
    archive_date: null,
  };

  /** Free-text filter applied to the already-loaded notes (client side). */
  notesSearch: string = '';
  /** Active association-type filter chip ('all' shows every note). */
  activeTypeFilter: string = 'all';
  /** Ids of notes whose description is currently expanded ("Show more"). */
  expandedNotes = new Set<string>();
  /** When true, note cards show a checkbox so individual notes can be selected for copying. */
  isSelectMode: boolean = false;
  /** Ids of notes currently ticked in select mode. */
  selectedNoteIds = new Set<string>();
  /** A note longer than this many plain-text chars gets a Show more/less toggle. */
  private readonly NOTE_CLAMP_THRESHOLD = 180;

  /** True while the share overlay is open. */
  isShare: boolean = false;
  /** The note the share overlay is acting on. */
  shareTarget: any = null;
  /** Staff of the current office, used as the "Users" dropdown options. */
  shareUsers: any[] = [];
  /** Teams of the current office, used as the "Teams" dropdown options. */
  shareTeams: any[] = [];
  /** Ids ticked in the Users dropdown. */
  selectedShareUsers: string[] = [];
  /** Ids ticked in the Teams dropdown. */
  selectedShareTeams: string[] = [];
  /** True while the share request is in flight (disables the Share button). */
  isSharing: boolean = false;

  searchSubject = new Subject<any>();
  /** Debounced trigger for the server-side notes search box. */
  notesSearchSubject = new Subject<string>();
  private subscriptions = new Subscription();

  constructor(
    public alertService: AlertService,
    public router: Router,
    public Api: ApiService,
    public redux: ReduxService,
    public voice: AiAssistantVoiceService,
    public toastr: ToastrService,
    public sharedService: SharedService,
    private remmiConfirm: RemmiConfirmService
  ) {}

  ngOnInit() {
    // Debounced association search while typing in a note.
    // - Fires only when more than 3 characters have been typed.
    // - switchMap cancels any previous in-flight request before starting a new one.
    this.subscriptions.add(
      this.searchSubject
        .pipe(
          debounceTime(400),
          map(value => (value ?? '').toString().trim()),
          distinctUntilChanged(),
          switchMap(value => {
            if (value.length <= 2) {
              this.searchBar = [];
              return EMPTY;
            }
            return this.associationSearch(value).pipe(catchError(() => of(null)));
          })
        )
        .subscribe(res => res && this.applyAssociationResult(res))
    );

    // Debounced server-side notes search (resets to page 1 with the current keyword/type).
    this.subscriptions.add(this.notesSearchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => this.getAllNotes()));

    // Refresh notes whenever a "latest notes" alert is broadcast
    this.subscriptions.add(
      this.alertService.latestNotes$.subscribe(() => {
        this.redux.setNotes(null);
        this.getAllNotes();
      })
    );

    // Open the panel on external request (e.g. from a lead screen), optionally scoped to a project.
    this.subscriptions.add(
      this.alertService.openNotesPanel$.subscribe(({ projectId, leadName, leadEmail }) => this.openWithProject(projectId, leadName, leadEmail))
    );

    this.getAllNotes();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.stopService();
  }

  onSearch() {
    this.searchSubject.next(this.note?.title);
  }

  openNote() {
    this.isNoteOpen = !this.isNoteOpen;
    if (this.isNoteOpen) {
      // Panel closed — drop any externally supplied project/lead scope.
      this.projectIdOverride = null;
      this.leadName = null;
      this.leadEmail = null;
    } else {
      // The header is persistent across routes, so reload on open to pick up the
      // current page's project context (project_id) for the notes request.
      this.getAllNotes();
    }
  }

  /**
   * Opens the panel scoped to a specific project (e.g. requested from a lead screen).
   * The override takes precedence over the URL-derived project id.
   */
  openWithProject(projectId?: string | null, leadName?: string | null, leadEmail?: string | null) {
    this.projectIdOverride = projectId || null;
    this.leadName = leadName || null;
    this.leadEmail = leadEmail || null;
    this.notesSearch = '';
    this.activeTypeFilter = 'all';
    this.isNoteOpen = false; // show the panel
    this.getAllNotes();
  }

  isCloseNotes() {
    this.isNoteOpen = true;
    setTimeout(() => {
      this.router.navigateByUrl('/personal-notes');
    }, 1500);
  }

  filterNote = eve => {
    /**
     * Get the global search results.
     * Only searches when more than 3 characters have been entered.
     * @param eve The search text.
     */
    const value = (eve ?? '').toString().trim();
    if (value.length <= 3) {
      this.searchBar = [];
      return;
    }
    this.associationSearch(value).subscribe(res => {
      this.applyAssociationResult(res);
      // If the title was cleared while the request was in flight, drop the results.
      setTimeout(() => {
        if (!this.note.title) {
          this.searchBar = [];
        }
      }, 1000);
    });
  };

  /** Builds the global-search request used to find note associations. */
  private associationSearch(value: string) {
    return this.Api.get('/global-search/?office_id=' + this.Api.getOfficeId() + '&property_address=' + value);
  }

  /** Maps a global-search response into the grouped searchBar shown under the note title. */
  private applyAssociationResult = res => {
    /**
     * Create an empty array to store the result
     */
    let bar: any[] = [];

    /**
     * Create a mapping object to define the keys for each type of data
     */
    const mapping = {
      propertyData: { type: 'property', titleKey: 'property_address' },
      listingData: { type: 'listing', titleKey: 'property_address' },
      contacts: { type: 'contacts', titleKey: 'full_name' },
      // contracts: { type: 'contract', titleKey: 'property_address' },
      projects: { type: 'projects', titleKey: 'Project_Name' },
      units: { type: 'units', titleKey: 'lot_name' },
      // tasks: { type: 'tasks', titleKey: 'title', extraKey: 'task_status' },
      leads: { type: 'leads', titleKey: 'full_name', extraKey: 'lead_type' },
      eoi: { type: 'eoi', titleKey: 'project_name', extraKey: 'status_reason' },
    };

    /**
     * Loop through the result and create an array of objects with the required data
     */
    (res?.data || []).forEach(f => {
      Object.keys(mapping).forEach(key => {
        if (f?.[key]?.length > 0) {
          f[key].forEach(g => {
            bar.push({
              title: g[mapping[key].titleKey] + (mapping[key].extraKey ? ` (${g[mapping[key].extraKey]})` : ''),
              id: g._id,
              type: mapping[key].type,
              icon: this.search_notes_type.find(item => item.type === mapping[key].type)?.icon || '',
              wdth: this.search_notes_type.find(item => item.type === mapping[key].type)?.wdth || '',
              margin: this.search_notes_type.find(item => item.type === mapping[key].type)?.margin || '',
              isImg: true,
            });
          });
        }
      });
    });
    /**
     * Update the search_notes_type array with the data from the result
     */
    this.search_notes_type.forEach(el => {
      el['data'] = bar.filter(type => el.type === type.type) || [];
    });
    /**
     * Set the search bar with the updated data
     */
    // Show the groups that actually have matches first; empty ones drop to the bottom.
    // Among populated groups, force a fixed order: projects -> listing -> contacts, then the rest.
    // (A copy so the underlying search_notes_type / filter-tab order isn't disturbed.)
    const groupOrder = ['projects', 'listing', 'contacts'];
    const groupRank = (t: string) => {
      const i = groupOrder.indexOf(t);
      return i === -1 ? groupOrder.length : i;
    };
    this.searchBar = [...this.search_notes_type].sort((a: any, b: any) => {
      const dataDiff = (b?.data?.length ? 1 : 0) - (a?.data?.length ? 1 : 0);
      if (dataDiff !== 0) {
        return dataDiff;
      }
      return groupRank(a?.type) - groupRank(b?.type);
    });
    this.highlightedIndex = -1;
  };

  /** Flattened list of selectable suggestions, in the same order they render (for keyboard nav). */
  get flatSearchItems(): any[] {
    return (this.searchBar || []).filter((g: any) => g?.type !== 'other').flatMap((g: any) => g?.data || []);
  }

  /** True when the given suggestion is the one currently highlighted via the keyboard. */
  isHighlighted(ele: any): boolean {
    return this.highlightedIndex >= 0 && this.flatSearchItems[this.highlightedIndex] === ele;
  }

  /** Arrow Up/Down to move through suggestions, Enter to pick, Escape to dismiss. */
  onSearchKeydown(event: KeyboardEvent) {
    const items = this.flatSearchItems;
    if (event.key === 'Escape') {
      this.searchBar = [];
      this.highlightedIndex = -1;
      return;
    }
    if (!items.length) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedIndex = (this.highlightedIndex + 1) % items.length;
      this.scrollHighlightedIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex = (this.highlightedIndex - 1 + items.length) % items.length;
      this.scrollHighlightedIntoView();
    } else if (event.key === 'Enter' && this.highlightedIndex >= 0) {
      event.preventDefault();
      this.addItem(items[this.highlightedIndex]);
    }
  }

  /** Keep the keyboard-highlighted suggestion visible inside the scrolling dropdown. */
  private scrollHighlightedIntoView() {
    // Defer so Angular applies the .highlighted class before we measure/scroll.
    setTimeout(() => {
      const el = document.querySelector('.list_ .add-hover.highlighted') as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  addItem = item => {
    this.note.title = item.title;
    this.note['id'] = item.id;
    this.note.type = item.type;
    this.searchBar = [];
    this.highlightedIndex = -1;
  };

  isEditNotes(note) {
    this.isEdit = true;
    console.log('note >>;', note);
    this.note = note;
  }

  _onClickOut(e: Event) {
    if (e['bol'] == false) {
      this.searchBar = [];
    }
  }

  saveNotes = () => {
    /**
     * Save the note details and send a POST request to the server.
     */
    this.submitted = true;
    if (!this.note.title) {
      return false;
    }

    let description = this.customEditorAddNote.nativeElement['innerHTML'] ? this.customEditorAddNote.nativeElement['innerHTML'] : '';
    let payload = {
      user_id: this.Api.getUserId(),
      office_id: this.Api.getOfficeId(),
      title: this.note.title,
      desc: description,
      id: this.note.id,
      type: this.note.type,
      isPin: false,
      is_archive: false,
      archive_date: null,
    };
    // Send a POST request to save the note
    this.Api.post('/user-notes', payload).subscribe({
      next: _ => {
        // Notify the user of successful addition
        this.toastr.success('Added successfully');
        this.submitted = false;
        this.isNote = false;
        this.alertService.noteAddedUpdate(true);
        // Refresh the list of notes
        this.redux.setNotes(null);
        this.getAllNotes();
        // Clear the input box
        this.clearNotes();
      },
    });
  };

  setAddStyle(style: string) {
    this.customEditorAddNote.nativeElement.focus();
    document.execCommand(style, true, null);
  }

  updateNote = note => {
    /**
     * Updates a note with new information.
     *
     * @param note - The note object to be updated.
     * @param idx - The index or flag to indicate archival status.
     */
    this.submitted = true;
    if (!note._id || !note.title) {
      // Exit if the note does not have a valid ID
      return;
    }

    // // Set the note's archive status and update the archive date

    let description = this.customEditorEditNote.nativeElement['innerHTML'] ? this.customEditorEditNote.nativeElement['innerHTML'] : '';
    let payload = {
      office_id: this.Api.getOfficeId(),
      user_id: this.Api.getUserId(),
      title: this.note.title,
      desc: description,
      id: this.note.id,
      type: this.note.type,
      isPin: false,
      is_archive: false,
      archive_date: null,
    };
    // Send updated note data to the server
    this.Api.put(`/user-notes/${note._id}`, payload).subscribe(() => {
      // Set note to non-editable after successful update
      this.submitted = false;
      // Display success message
      this.toastr.success('Updated successfully');

      // Trigger an update event
      this.alertService.noteAddedUpdate(true);

      this.redux.setNotes(null);
      // Refresh the list of notes
      this.getAllNotes();
      this.clearNotes();
    });
  };

  pinNote = (note, bool) => {
    /**
     * @param note - The note object to be updated.
     */
    this.submitted = true;
    if (!note._id) {
      // Exit if the note does not have a valid ID
      return;
    }
    // Set the note's archive status and update the archive date
    let payload = {
      office_id: this.Api.getOfficeId(),
      user_id: this.Api.getUserId(),
      title: note.title,
      desc: note.desc,
      id: note.id,
      type: note.type,
      isPin: bool,
      is_archive: false,
      archive_date: null,
    };
    // Send updated note data to the server
    this.Api.put(`/user-notes/${note._id}`, payload).subscribe(() => {
      // Display success message
      this.toastr.success('Updated successfully');
      // Trigger an update event
      this.alertService.noteAddedUpdate(true);
      this.redux.setNotes(null);
      // Refresh the list of notes
      this.getAllNotes();
    });
  };

  archiveNote = note => {
    /**
     * @param note - The note object to be updated.
     */
    if (!note._id) {
      // Exit if the note does not have a valid ID
      return;
    }
    // // Set the note's archive status and update the archive date
    let payload = {
      office_id: this.Api.getOfficeId(),
      user_id: this.Api.getUserId(),
      title: note.title,
      desc: note.desc,
      id: note.id,
      type: note.type,
      isPin: note.isPin,
      is_archive: true,
      archive_date: new Date(),
    };
    // Send updated note data to the server
    this.Api.put(`/user-notes/${note._id}`, payload).subscribe(() => {
      // Display success message
      this.toastr.success('Updated successfully');
      // Trigger an update event
      this.alertService.noteAddedUpdate(true);
      this.redux.setNotes(null);
      // Refresh the list of notes
      this.getAllNotes();
    });
  };

  setEditStyle(style: string) {
    this.customEditorEditNote.nativeElement.focus();
    document.execCommand(style, true, null);
  }

  clearNotes = () => {
    this.isEdit = false;
    this.note = {
      title: '',
      desc: '',
      id: null,
      isPin: false,
      type: null,
      is_archive: false,
      archive_date: null,
    };
    this.searchBar = [];
    if (this.customEditorAddNote?.nativeElement) {
      this.customEditorAddNote.nativeElement.innerHTML = '';
    }

    if (this.customEditorEditNote?.nativeElement) {
      this.customEditorEditNote.nativeElement.innerHTML = '';
    }
  };

  /**
   * Starts voice dictation, live-updating the editor with the transcript.
   * Mirrors the AI assistant composer: a single SpeechRecognition session that emits
   * interim + final results and auto-stops when the user stops speaking.
   * @param bool true for the "add note" editor, false for the "edit note" editor.
   */
  startService(bool: boolean = false) {
    if (this.listening) {
      this.stopService();
      return;
    }
    if (!this.voice.isSupported) {
      this.toastr.error('Voice typing is not supported in this browser. Try Google Chrome.');
      return;
    }

    const editor = bool ? this.customEditorAddNote : this.customEditorEditNote;
    // Snapshot existing content so dictation is appended, not replaced.
    const existing = (editor?.nativeElement?.['innerHTML'] || '').trim();
    this.htmlBeforeVoice = existing ? existing + ' ' : '';
    this.listening = true;

    this.voiceSub = this.voice.listen().subscribe({
      next: result => {
        if (editor?.nativeElement) {
          editor.nativeElement['innerHTML'] = (this.htmlBeforeVoice + result.text).trimStart();
        }
      },
      error: err => {
        this.listening = false;
        this.toastr.error(this.voiceErrorCopy(err));
      },
      complete: () => {
        this.listening = false;
      },
    });
  }

  stopService() {
    this.voice.stop();
    this.voiceSub?.unsubscribe();
    this.voiceSub = undefined;
    this.listening = false;
  }

  /** Maps a SpeechRecognition error to user-facing copy (same wording as the AI assistant). */
  private voiceErrorCopy(err: unknown): string {
    const code = (err as any)?.message || '';
    if (code === 'not-allowed' || code === 'service-not-allowed') {
      return 'Microphone access was blocked. Allow mic permission to use voice input.';
    }
    if (code === 'no-speech') {
      return 'No speech detected. Try again.';
    }
    return 'Voice input is unavailable right now.';
  }

  /**
   * When viewing a project edit page (/projects/edit_/<id>/...), pull the project id
   * out of the URL so notes can be scoped to that project.
   */
  getProjectIdFromUrl(): string | undefined {
    const match = (this.router.url || '').match(/\/projects\/edit_\/([^/?#]+)/);
    return match ? match[1] : undefined;
  }

  /** The project id in effect: an external override (e.g. from a lead) wins, else the URL. */
  getActiveProjectId(): string | undefined {
    return this.projectIdOverride || this.getProjectIdFromUrl();
  }

  getAllNotes = (append = false) => {
    /**
     * Retrieves a page of user notes from the server (infinite scroll).
     * Loads `perPage` (10) notes at a time; onScrollA loads the next page until
     * the server returns a partial page.
     *
     * @param {boolean} append - If true, append the next page; otherwise reset to page 1.
     */
    if (!append) {
      this.page = 1;
      this.hasMoreNotes = true;
    }
    this.isLoading = true;
    const projectId = this.getActiveProjectId();
    const keyword = this.notesSearch?.trim() || '';
    // Filter chip -> type param: 'all' => null, 'other' => '' (empty), else the type string.
    const type = this.activeTypeFilter === 'all' ? null : this.activeTypeFilter === 'other' ? '' : this.activeTypeFilter;
    this.redux.getUserNotes(true, projectId, this.page, this.perPage, keyword, type).subscribe({
      next: response => {
        const res = JSON.parse(JSON.stringify(response || {}));
        const raw: any[] = res['data'] || [];
        // A full page back means there may be more; a short page means we're done.
        this.hasMoreNotes = raw.length === this.perPage;
        // Filter out archived notes for display, then precompute plain text for search/clamp.
        const pageNotes = raw.filter(x => !x?.is_archive);
        this.decorateNotes(pageNotes);
        this.notesList = append ? [...this.notesList, ...pageNotes] : pageNotes;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  };

  sortArrayByIsPin(array) {
    array.sort((a, b) => {
      if (a.isPin === b.isPin) {
        return 0; // Leave order unchanged if isPin values are equal
      } else if (a.isPin) {
        return -1; // a comes first if its isPin is true
      } else {
        return 1; // b comes first if its isPin is true
      }
    });
    return array;
  }

  /**
   * Notes to render: the loaded list filtered by the search box (title or body)
   * and sorted with pinned notes first. Search is client side over already
   * loaded notes — see note in getDisplayedNotes for server-side search.
   */
  getDisplayedNotes() {
    // Search and type filtering are server-side now; here we only keep pinned notes on top.
    return this.sortArrayByIsPin([...(this.notesList || [])]);
  }

  /** Fixed set of association-type chips shown under the search box. */
  getNoteTypeFilters(): string[] {
    return this.search_notes_type.map(t => t.type);
  }

  setTypeFilter(type: string) {
    if (this.activeTypeFilter === type) {
      return;
    }
    this.activeTypeFilter = type;
    // Re-query the server with the selected type (resets to page 1).
    this.getAllNotes();
  }

  /** Push the current search text to the debounced server-side search. */
  onNotesSearchChange() {
    this.notesSearchSubject.next(this.notesSearch || '');
  }

  /** Human label for a note type used on the filter chips. */
  typeLabel(type: string): string {
    if (type === 'units') {
      return 'Lots';
    }
    if (type === 'eoi') {
      return 'EOI';
    }
    return type;
  }

  clearNotesSearch() {
    this.notesSearch = '';
    this.getAllNotes();
  }

  /** True while a project is in context (URL or external override) — drives the "Copy project notes" button. */
  get isProjectPage(): boolean {
    return !!this.getActiveProjectId();
  }

  /**
   * Notes that belong to the currently open project: their association type is a
   * project AND their linked id matches the project id in the URL.
   */
  getProjectNotes(): any[] {
    const projectId = this.getActiveProjectId();
    if (!projectId) {
      return [];
    }
    return (this.notesList || []).filter(n => {
      return n?.type === 'projects' && String(n?.id) === String(projectId);
    });
  }

  /**
   * Copies this project's notes to the clipboard as a ready-to-send email.
   * [Agent Name] is left as a placeholder for the sender to fill in; the signature
   * uses the logged-in user's name.
   */
  copyProjectNotes() {
    const content = this.getProjectNotes()
      .map(n => (n?._plain || this.stripHtml(n?.desc) || '').trim())
      .filter(Boolean)
      .join('\n\n');

    if (!content) {
      this.toastr.error('There are no project notes to copy');
      return;
    }

    const email = `Hi ${this.leadName || '[Agent Name]'},\n\nAs requested.\n\n${content}`;

    this.copyToClipboard(email);
    this.toastr.success('Project notes copied to clipboard');
  }

  /** Toggle select mode; leaving it always clears the current selection. */
  toggleSelectMode() {
    this.isSelectMode = !this.isSelectMode;
    if (!this.isSelectMode) {
      this.selectedNoteIds.clear();
    }
  }

  /** Tick / untick a single note in select mode. */
  toggleNoteSelection(item: any) {
    const id = item?._id;
    if (!id) {
      return;
    }
    if (this.selectedNoteIds.has(id)) {
      this.selectedNoteIds.delete(id);
    } else {
      this.selectedNoteIds.add(id);
    }
  }

  isNoteSelected(item: any): boolean {
    return this.selectedNoteIds.has(item?._id);
  }

  /** True when every currently displayed note is selected. */
  areAllSelected(): boolean {
    const notes = this.getDisplayedNotes();
    return notes.length > 0 && notes.every((n: any) => this.selectedNoteIds.has(n?._id));
  }

  /** Select-all / clear-all toggle for the notes currently shown. */
  toggleSelectAll() {
    if (this.areAllSelected()) {
      this.selectedNoteIds.clear();
    } else {
      this.getDisplayedNotes().forEach((n: any) => n?._id && this.selectedNoteIds.add(n._id));
    }
  }

  /**
   * Copies the ticked notes to the clipboard as a ready-to-send email, using the
   * same format as copyProjectNotes ([Agent Name] placeholder + user signature).
   */
  copySelectedNotes() {
    const content = this.getDisplayedNotes()
      .filter((n: any) => this.selectedNoteIds.has(n?._id))
      .map((n: any) => (n?._plain || this.stripHtml(n?.desc) || '').trim())
      .filter(Boolean)
      .join('\n\n');

    if (!content) {
      this.toastr.error('Please select at least one note to copy');
      return;
    }

    const email = `Hi ${this.leadName || '[Agent Name]'},\n\nAs requested.\n\n${content}`;

    this.copyToClipboard(email);
    this.toastr.success('Selected notes copied to clipboard');
    // Done — leave select mode and reset the ticks.
    this.isSelectMode = false;
    this.selectedNoteIds.clear();
  }

  /**
   * Opens the default mail client with the notes pre-filled in the body, using the
   * same format as the copy features. Uses the ticked notes when any are selected,
   * otherwise every currently displayed note.
   */
  emailNotes() {
    const source =
      this.selectedNoteIds.size > 0 ? this.getDisplayedNotes().filter((n: any) => this.selectedNoteIds.has(n?._id)) : this.getDisplayedNotes();

    const content = source
      .map((n: any) => (n?._plain || this.stripHtml(n?.desc) || '').trim())
      .filter(Boolean)
      .join('\n\n');

    if (!content) {
      this.toastr.error('There are no notes to email');
      return;
    }

    const body = `Hi ${this.leadName || '[Agent Name]'},\n\nAs requested.\n\n${content}`;
    const recipient = this.leadEmail ? encodeURIComponent(this.leadEmail) : '';
    const mailto = `mailto:${recipient}?subject=${encodeURIComponent('Notes')}&body=${encodeURIComponent(body)}`;

    // Trigger the mail client without navigating the SPA away.
    const a = document.createElement('a');
    a.href = mailto;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * Opens the share overlay for a single note. Options are fetched once and then
   * reused for every subsequent open.
   */
  openShare(note: any) {
    this.shareTarget = note;
    this.selectedShareUsers = [];
    this.selectedShareTeams = [];
    this.isShare = true;
    if (!this.shareUsers.length) {
      this.getShareUsers();
    }
    if (!this.shareTeams.length) {
      this.getShareTeams();
    }
  }

  closeShare() {
    this.isShare = false;
    this.shareTarget = null;
    this.selectedShareUsers = [];
    this.selectedShareTeams = [];
  }

  /** Office staff for the Users dropdown (the logged-in user can't share with themselves). */
  private getShareUsers() {
    const url = '/users/byoffice?office=' + this.Api.getOfficeId() + '&show=staff';
    this.redux.getSetProjectInfo(false, 'byOffice', url).subscribe({
      next: response => {
        const currentUserId = this.Api.getUserId();
        this.shareUsers = (response?.data || [])
          .filter((u: any) => String(u?._id) !== String(currentUserId))
          .map((u: any) => ({ ...u, full_name: `${u?.first_name || ''} ${u?.last_name || ''}`.trim() }));
      },
      error: () => {
        this.shareUsers = [];
      },
    });
  }

  /** Office teams for the Teams dropdown. */
  private getShareTeams() {
    this.sharedService.getDropDownItems('teams').subscribe({
      next: res => {
        this.shareTeams = res?.data || [];
      },
      error: () => {
        this.shareTeams = [];
      },
    });
  }

  /** True once at least one user or team is picked — gates the Share button. */
  get canShare(): boolean {
    return this.selectedShareUsers.length > 0 || this.selectedShareTeams.length > 0;
  }

  /** Shares the selected note with the chosen users / teams. */
  submitShare() {
    if (!this.shareTarget?._id) {
      return;
    }
    if (!this.canShare) {
      this.toastr.error('Please select at least one user or team');
      return;
    }

    const payload = {
      office_id: this.Api.getOfficeId(),
      user_id: this.Api.getUserId(),
      note_id: this.shareTarget._id,
      shared_with_users: this.selectedShareUsers,
      shared_with_teams: this.selectedShareTeams,
    };

    this.isSharing = true;
    this.Api.post('/user-notes/share', payload).subscribe({
      next: () => {
        this.isSharing = false;
        this.toastr.success('Note shared successfully');
        this.closeShare();
      },
      error: () => {
        this.isSharing = false;
      },
    });
  }

  private copyToClipboard(text: string) {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  private fallbackCopy(text: string) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }

  toggleExpand(item: any) {
    const id = item?._id;
    if (!id) {
      return;
    }
    if (this.expandedNotes.has(id)) {
      this.expandedNotes.delete(id);
    } else {
      this.expandedNotes.add(id);
    }
  }

  isExpanded(item: any): boolean {
    return this.expandedNotes.has(item?._id);
  }

  /** True when a note is long enough to warrant clamping + a Show more toggle. */
  isLongNote(item: any): boolean {
    return (item?._plain?.length || 0) > this.NOTE_CLAMP_THRESHOLD;
  }

  /** Precompute a plain-text version of each note's HTML body (used for search + clamp checks). */
  private decorateNotes(notes: any[]) {
    (notes || []).forEach(n => {
      n._plain = this.stripHtml(n?.desc);
    });
  }

  private stripHtml(html: string): string {
    if (!html) {
      return '';
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  }

  deleteNote = (note: any) => {
    /**
     * Deletes a note from the server
     * @param note - The note object to be deleted.
     */
    if (!note._id) {
      // Exit if the note does not have a valid ID
      return;
    }
    this.remmiConfirm
      .delete({ title: 'Delete Note?', message: 'This note will be permanently deleted.', subject: note.title })
      .subscribe(isConfirmed => {
        if (!isConfirmed) {
          return;
        }
        // Send a delete request to the server
        this.Api.delete(`/user-notes/${note._id}`).subscribe(() => {
          // Refresh the list of notes after successful deletion
          this.redux.setNotes(null);
          this.getAllNotes();
        });
      });
  };

  scrollToTopA(): void {
    const element = this.scrollableElement.nativeElement as HTMLElement;
    element.scrollTop = 0;
  }

  onScrollA(event: Event): void {
    const element = event.target as HTMLElement;
    this.scrollTop = element.scrollTop;
    const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    // Near the bottom, more notes available, and no request already running.
    if (distanceToBottom < 80 && this.hasMoreNotes && !this.isLoading) {
      this.page = this.page + 1;
      this.getAllNotes(true);
    }
  }
}
